import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { cva } from 'class-variance-authority'
import { ExternalLinkIcon } from 'lucide-react'
import { ReactNode } from 'react'

import { Token } from '@/domain/types/Token'
import EligibleIcon from '@/ui/assets/icons/success.svg?react'
import NotEligibleIcon from '@/ui/assets/icons/x.svg?react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/ui/atoms/accordion/Accordion'
import { Badge } from '@/ui/atoms/badge/Badge'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { links } from '@/ui/constants/links'
import { Info } from '@/ui/molecules/info/Info'
import { testIds } from '@/ui/utils/testIds'
import { useIsTruncated } from '@/ui/utils/useIsTruncated'

type AirdropOrigin = 'pre-farming' | 'spark-ignition' | 'social-campaign' | 'cookie3' | 'spark-overdrive'

export interface AirdropEligibilityBreakdownProps {
  airdrops: {
    origin: AirdropOrigin
    amount: NormalizedNumber
    status: 'eligible' | 'ineligible' | 'claimed' | 'expired'
    detailedBreakdown?: {
      label: string
      amount: NormalizedNumber
    }[]
  }[]
  spk: Token
}

export function AirdropEligibilityBreakdown({ airdrops, spk }: AirdropEligibilityBreakdownProps) {
  return (
    <div className="flex flex-col">
      <div className="typography-label-3 text-secondary">Airdrop breakdown</div>
      <Accordion type="multiple">
        {airdrops.map((airdrop, index) => (
          <AccordionItem
            key={airdrop.origin}
            value={airdrop.origin}
            data-testid={testIds.spkAirdrop.eligibilityBreakdown.item(index)}
          >
            <AccordionTrigger>
              <EligibilityItem
                spk={spk}
                airdrop={airdrop.amount}
                status={airdrop.status}
                titleDataTestId={testIds.spkAirdrop.eligibilityBreakdown.title}
                amountDataTestId={testIds.spkAirdrop.eligibilityBreakdown.amount}
              >
                {airdropOriginToMetadata[airdrop.origin].label}
              </EligibilityItem>
            </AccordionTrigger>
            <AccordionContent className="pb-6">
              <div className="typography-body-4 mb-4 text-secondary">
                {airdropOriginToMetadata[airdrop.origin].description}
              </div>
              {airdrop.detailedBreakdown && airdrop.detailedBreakdown.length > 0 && (
                <div className="flex flex-col gap-3 rounded-sm bg-secondary p-4 sm:p-6">
                  {airdrop.detailedBreakdown.map(({ label, amount }, subIndex) => (
                    <EligibilityItem
                      key={label}
                      spk={spk}
                      airdrop={amount}
                      variant="subDetail"
                      dataTestId={testIds.spkAirdrop.eligibilityBreakdown.subItem(subIndex)}
                      titleDataTestId={testIds.spkAirdrop.eligibilityBreakdown.subItemTitle}
                      amountDataTestId={testIds.spkAirdrop.eligibilityBreakdown.subItemAmount}
                    >
                      {label}
                    </EligibilityItem>
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  )
}

function EligibilityItem({
  spk,
  airdrop,
  children,
  variant,
  status,
  dataTestId,
  titleDataTestId,
  amountDataTestId,
}: {
  spk: Token
  airdrop: NormalizedNumber
  status?: 'eligible' | 'ineligible' | 'claimed' | 'expired'
  children: ReactNode
  variant?: 'default' | 'subDetail'
  dataTestId?: string
  titleDataTestId?: string
  amountDataTestId?: string
}) {
  return (
    <div className={eligibilityItemVariants({ variant })} data-testid={dataTestId}>
      <div className="grid grid-cols-[auto_1fr] items-center gap-1 sm:gap-2">
        {airdrop.gt(0) ? (
          <EligibleIcon className="icon-system-success-primary icon-size-xs sm:icon-size-sm" />
        ) : (
          <NotEligibleIcon className="icon-tertiary icon-size-xs sm:icon-size-sm" />
        )}
        <EligibilityItemTitle dataTestId={titleDataTestId}>{children}</EligibilityItemTitle>
      </div>
      {(() => {
        switch (status) {
          case 'claimed':
            return (
              <Badge variant="success" appearance="soft" size="sm">
                Claimed
              </Badge>
            )
          case 'expired':
            return (
              <Badge variant="neutral" appearance="soft" size="sm">
                Expired{' '}
                <Info>
                  Deadline for claiming airdrop has passed.{' '}
                  <ExternalLink
                    to={links.docs.airdrop.summary}
                    variant="underline"
                    className="inline-flex items-center gap-1"
                  >
                    Learn more <ExternalLinkIcon className="icon-size-xxs" />
                  </ExternalLink>
                  .
                </Info>
              </Badge>
            )
          default:
            return null
        }
      })()}
      <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
        <TokenIcon token={spk} className="size-4" />
        <div className="text-primary" data-testid={amountDataTestId}>
          {spk.format(airdrop, { style: 'compact' })}
        </div>
      </div>
    </div>
  )
}

function EligibilityItemTitle({ children, dataTestId }: { children: ReactNode; dataTestId?: string }) {
  const [ref, isTruncated] = useIsTruncated()

  return (
    <Tooltip open={!isTruncated ? false : undefined}>
      <TooltipTrigger asChild>
        <div className="truncate" ref={ref} data-testid={dataTestId}>
          {children}
        </div>
      </TooltipTrigger>
      <TooltipContent>{children}</TooltipContent>
    </Tooltip>
  )
}

const eligibilityItemVariants = cva('flex w-full items-center gap-3', {
  variants: {
    variant: {
      default: 'typography-label-3 sm:typography-label-2 py-6',
      subDetail: 'typography-label-3',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

const airdropOriginToMetadata: Record<AirdropOrigin, { label: string; description: string }> = {
  'pre-farming': {
    label: 'SparkLend and AAVE pre-farming',
    description: 'Airdrop for users who participated in the SparkLend and AAVE pre-farming campaigns.',
  },
  'spark-ignition': {
    label: 'Spark Ignition',
    description: 'Airdrop for users who qualified for one or more Ignition criteria.',
  },
  'social-campaign': {
    label: 'Social Campaign',
    description: 'Airdrop for users who participated in the social media campaign.',
  },
  cookie3: {
    label: 'Cookie DAO',
    description: 'Airdrop for users who participated in the Cookie DAO campaign.',
  },
  'spark-overdrive': {
    label: 'Spark Overdrive',
    description: 'Airdrop for users who participated in the Spark Ignition campaign and earned Overdrive airdrop.',
  },
}
